import React, { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import AddressCard from "./component/AddressCard";
import ConfirmationModal from "../../components/modal/ConfirmationModal";
import { appApiHandler } from "../../api/appApiHandler";
import EmptyState from "../../components/EmptyState";
import FullscreenLoader from "../../components/loader/fullScreenLoader/FullscreenLoader";
import { showErrorToast, showSuccessToast } from "../../utils/toast";
import { FiMapPin, FiPlus } from "react-icons/fi";
import { string } from "../../constants/string";
import { getErrorMessage } from "../../constants/globalFunction";
import "./Address.css";
import HomeHeader from "../../components/header/HomeHeader";
import CommonFooter from "../../components/footer/CommonFooter";
import CommonButton from "../../components/button/CommonButton";
import Breadcrumb from "../../components/breadcrumb";
import { FaUser } from "react-icons/fa";
import InlineLoader from "../../components/loader/InlineLoader";
import CommonInput from "../../components/input/CommonInput";
import PhoneInput from "../../components/input/PhoneInput";
import CountryModal from "../../components/modal/CountryModal";

const Address = () => {
  const navigate = useNavigate();
  const { userData } = useSelector((state) => state.userState);
  const { isLoggedIn } = useSelector((state) => state.authState);

  const [addresses, setAddresses] = useState([]);
  const [modalVisible, setModalVisible] = useState(false);
  const [loading, setLoading] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [selectedAddress, setSelectedAddress] = useState(null);
  const [selectedAddressId, setSelectedAddressId] = useState(null);
  const [editingAddressId, setEditingAddressId] = useState(null);

  // Form state
  const [showAddForm, setShowAddForm] = useState(false);
  const [name, setName] = useState("");
  const [phoneno, setPhoneno] = useState("");
  const [countryCode, setCountryCode] = useState("+91");
  const [address, setAddress] = useState("");
  const [country, setCountry] = useState(null);
  const [city, setCity] = useState("");
  const [state, setState] = useState("");
  const [pincode, setPincode] = useState("");
  const [countryList, setCountryList] = useState([]);
  const [countryModalVisible, setCountryModalVisible] = useState(false);
  const [buttonLoading, setButtonLoading] = useState(false);

  // Edit form state (separate from add form)
  const [editName, setEditName] = useState("");
  const [editPhoneno, setEditPhoneno] = useState("");
  const [editCountryCode, setEditCountryCode] = useState("+91");
  const [editAddress, setEditAddress] = useState("");
  const [editCountry, setEditCountry] = useState(null);
  const [editCity, setEditCity] = useState("");
  const [editState, setEditState] = useState("");
  const [editPincode, setEditPincode] = useState("");

  // Form validation errors
  const [nameError, setNameError] = useState("");
  const [addressError, setAddressError] = useState("");
  const [phonenoError, setPhonenoError] = useState("");
  const [countryError, setCountryError] = useState("");
  const [cityError, setCityError] = useState("");
  const [stateError, setStateError] = useState("");
  const [pincodeError, setPincodeError] = useState("");

  const onEditAddress = (item) => {
    // If add form is open, close it and reset
    if (showAddForm) {
      setShowAddForm(false);
      // Directly reset add form state
      setName("");
      setPhoneno("");
      setCountryCode("+91");
      setAddress("");
      setCountry(null);
      setCity("");
      setState("");
      setPincode("");
      setNameError("");
      setAddressError("");
      setPhonenoError("");
      setCountryError("");
      setCityError("");
      setStateError("");
      setPincodeError("");
    }

    // Pre-fill the edit form with current address data
    const formData = {
      name: item.name || "",
      phoneno: item.contact || "",
      countryCode: item.country_code || "+91",
      address: item.address || "",
      country: item.country || null,
      city: item.city || "",
      state: item.state || "",
      pincode: item.pincode || ""
    };

    // Set edit form values
    setEditName(formData.name);
    setEditPhoneno(formData.phoneno);
    setEditCountryCode(formData.countryCode);
    setEditAddress(formData.address);
    setEditCountry(formData.country);
    setEditCity(formData.city);
    setEditState(formData.state);
    setEditPincode(formData.pincode);

    // Clear any existing errors
    setNameError("");
    setAddressError("");
    setPhonenoError("");
    setCountryError("");
    setCityError("");
    setStateError("");
    setPincodeError("");

    // Set the address being edited
    setEditingAddressId(item._id);
  };

  const onCancelEdit = () => {
    setEditingAddressId(null);
    // Directly reset edit form state
    setEditName("");
    setEditPhoneno("");
    setEditCountryCode("+91");
    setEditAddress("");
    setEditCountry(null);
    setEditCity("");
    setEditState("");
    setEditPincode("");
  };

  const onSubmitEditAddress = async () => {
    const isValid = validateEditValues();
    if (isValid) {
      const reqData = {
        user_id: userData?._id,
        address_id: editingAddressId,
        name: editName.trim(),
        contact: editPhoneno.trim(),
        country_code: editCountryCode,
        address: editAddress.trim(),
        country_id: editCountry?._id,
        pincode: editPincode.trim(),
        state: editState.trim(),
        city: editCity.trim(),
      };

      setButtonLoading(true);
      const response = await appApiHandler.updateAddress(reqData);

      setButtonLoading(false);
      if (response?.status === 200) {
        showSuccessToast("Address updated successfully");
        setEditingAddressId(null);
        // Directly reset edit form state
        setEditName("");
        setEditPhoneno("");
        setEditCountryCode("+91");
        setEditAddress("");
        setEditCountry(null);
        setEditCity("");
        setEditState("");
        setEditPincode("");
        getAddresses(response?.data?._id);
      } else if (response?.message) {
        showErrorToast(getErrorMessage(response?.message));
      } else {
        showErrorToast(string.error.failed_due_to_internal_error);
      }
    }
  };

  const onDeleteAddress = (address) => {
    setSelectedAddress(address);
    setModalVisible(true);
  };

  const onCloseModal = () => {
    setModalVisible(false);
  };

  const deleteAddress = async () => {
    setModalVisible(false);
    const reqData = {
      address_id: selectedAddress?._id,
    };
    setProcessing(true);
    const response = await appApiHandler.deleteAddress(reqData);
    setSelectedAddress(null);
    if (response?.status === 200) {
      showSuccessToast("Address deleted successfully");
      getAddresses();
    } else if (response?.message) {
      setProcessing(false);
      showErrorToast(getErrorMessage(response?.message));
    } else {
      setProcessing(false);
      showErrorToast(string.error.failed_due_to_internal_error);
    }
  };

  const onPressDeliverHere = (item) => {
    navigate("/checkout", { state: { address: item } });
  };

  const onSelectAddress = (item) => {
    // If clicking on already selected address, keep it selected
    // If clicking on different address, select the new one

    // If edit form is open, close it and reset
    if (editingAddressId) {
      setEditingAddressId(null);
      // Directly reset edit form state
      setEditName("");
      setEditPhoneno("");
      setEditCountryCode("+91");
      setEditAddress("");
      setEditCountry(null);
      setEditCity("");
      setEditState("");
      setEditPincode("");
    }

    setSelectedAddressId(item._id);
  };

  const getCountryList = async () => {
    const response = await appApiHandler.getCountries();
    if (response?.status === 200) {
      setCountryList(response?.data);
    }
  };

  const onAddNewAddress = () => {
    // If edit form is open, close it and reset
    if (editingAddressId) {
      setEditingAddressId(null);
      // Directly reset edit form state
      setEditName("");
      setEditPhoneno("");
      setEditCountryCode("+91");
      setEditAddress("");
      setEditCountry(null);
      setEditCity("");
      setEditState("");
      setEditPincode("");
    }

    setShowAddForm(!showAddForm);
    if (!showAddForm) {
      // Reset form when opening
      resetForm();
    }
  };

  const onCancelForm = () => {
    // Close add form
    setShowAddForm(false);
    resetForm();

    // Close edit form if open
    if (editingAddressId) {
      setEditingAddressId(null);
      // Directly reset edit form state
      setEditName("");
      setEditPhoneno("");
      setEditCountryCode("+91");
      setEditAddress("");
      setEditCountry(null);
      setEditCity("");
      setEditState("");
      setEditPincode("");
    }
  };

  const resetForm = () => {
    setName("");
    setPhoneno("");
    setCountryCode("+91");
    setAddress("");
    setCountry(null);
    setCity("");
    setState("");
    setPincode("");
    setNameError("");
    setAddressError("");
    setPhonenoError("");
    setCountryError("");
    setCityError("");
    setStateError("");
    setPincodeError("");
  };

  const resetEditForm = () => {
    setEditName("");
    setEditPhoneno("");
    setEditCountryCode("+91");
    setEditAddress("");
    setEditCountry(null);
    setEditCity("");
    setEditState("");
    setEditPincode("");
  };

  const onSelectCountry = (selectedCountry) => {
    setCountry(selectedCountry);
    setCountryModalVisible(false);
  };

  const onSelectEditCountry = (selectedCountry) => {
    setEditCountry(selectedCountry);
    setCountryModalVisible(false);
  };

  const onSelectCountryHandler = (selectedCountry) => {
    if (editingAddressId) {
      // Edit mode
      onSelectEditCountry(selectedCountry);
    } else {
      // Add mode
      onSelectCountry(selectedCountry);
    }
  };

  const onPressCountry = () => {
    setCountryModalVisible(true);
  };

  const onPressEditCountry = () => {
    setCountryModalVisible(true);
  };

  const onCloseCountryModal = () => {
    setCountryModalVisible(false);
  };

  const validateValues = () => {
    let isValid = true;

    if (!userData) {
      showErrorToast(string.error.please_login_to_your_account);
      return false;
    }

    if (!name.trim()) {
      setNameError("Please enter name");
      isValid = false;
    } else {
      setNameError("");
    }

    if (!phoneno.trim()) {
      setPhonenoError("Please enter contact number");
      isValid = false;
    } else {
      setPhonenoError("");
    }

    if (countryCode === "+91") {
      const digitsOnlyLength = phoneno.trim().length;
      if (digitsOnlyLength !== 10) {
        setPhonenoError("Please enter valid phone number");
        isValid = false;
      }
    }

    if (!address.trim()) {
      setAddressError("Please enter address");
      isValid = false;
    } else {
      setAddressError("");
    }

    if (!country) {
      setCountryError("Please select country");
      isValid = false;
    } else {
      setCountryError("");
    }

    if (!pincode.trim()) {
      setPincodeError("Please enter pincode");
      isValid = false;
    } else {
      setPincodeError("");
    }

    if (!city.trim()) {
      setCityError("Please enter city");
      isValid = false;
    } else {
      setCityError("");
    }

    if (!state.trim()) {
      setStateError("Please enter state");
      isValid = false;
    } else {
      setStateError("");
    }

    return isValid;
  };

  const validateEditValues = () => {
    let isValid = true;

    if (!userData) {
      showErrorToast(string.error.please_login_to_your_account);
      return false;
    }

    if (!editName.trim()) {
      setNameError("Please enter name");
      isValid = false;
    } else {
      setNameError("");
    }

    if (!editPhoneno.trim()) {
      setPhonenoError("Please enter contact number");
      isValid = false;
    } else {
      setPhonenoError("");
    }

    if (editCountryCode === "+91") {
      const digitsOnlyLength = editPhoneno.trim().length;
      if (digitsOnlyLength !== 10) {
        setPhonenoError("Please enter valid phone number");
        isValid = false;
      }
    }

    if (!editAddress.trim()) {
      setAddressError("Please enter address");
      isValid = false;
    } else {
      setAddressError("");
    }

    if (!editCountry) {
      setCountryError("Please select country");
      isValid = false;
    } else {
      setCountryError("");
    }

    if (!editPincode.trim()) {
      setPincodeError("Please enter pincode");
      isValid = false;
    } else {
      setPincodeError("");
    }

    if (!editCity.trim()) {
      setCityError("Please enter city");
      isValid = false;
    } else {
      setCityError("");
    }

    if (!editState.trim()) {
      setStateError("Please enter state");
      isValid = false;
    } else {
      setStateError("");
    }

    return isValid;
  };

  const onSubmitAddress = async () => {
    const isValid = validateValues();
    if (isValid) {
      const reqData = {
        user_id: userData?._id,
        name: name.trim(),
        contact: phoneno.trim(),
        country_code: countryCode,
        address: address.trim(),
        country_id: country?._id,
        pincode: pincode.trim(),
        state: state.trim(),
        city: city.trim(),
      };

      setButtonLoading(true);
      const response = await appApiHandler.addAddress(reqData);

      setButtonLoading(false);
      if (response?.status === 200) {
        showSuccessToast("Address added successfully");
        resetForm();
        setShowAddForm(false);
        getAddresses(response?.data?._id);
      } else if (response?.message) {
        showErrorToast(getErrorMessage(response?.message));
      } else {
        showErrorToast(string.error.failed_due_to_internal_error);
      }
    }
  };

  const getAddresses = async (addressId) => {
    const reqData = {
      user_id: userData?._id,
    };
    const response = await appApiHandler.getAddress(reqData);
    if (response?.status === 200 && response?.data) {
      setAddresses(response?.data);
      // Auto-select the first address if available
      if (response?.data?.length > 0) {
        setSelectedAddressId(addressId || response?.data[0]?._id);
      }
    }
    setLoading(false);
    setProcessing(false);
  };

  useEffect(() => {
    if (isLoggedIn) {
      setLoading(true);
      Promise.all([getAddresses(), getCountryList()]).then(() => {
        setLoading(false);
      });
    }
  }, [isLoggedIn]);

  const renderAddresses = useCallback(
    (item, index) => {
      return (
        <AddressCard
          key={item._id || index}
          item={item}
          onEdit={onEditAddress}
          onDelete={onDeleteAddress}
          onDeliverHere={onPressDeliverHere}
          isSelected={selectedAddressId === item._id}
          onSelect={onSelectAddress}
          isEditing={editingAddressId === item._id}
          // Edit form props
          name={editName}
          setName={setEditName}
          phoneno={editPhoneno}
          setPhoneno={setEditPhoneno}
          countryCode={editCountryCode}
          setCountryCode={setEditCountryCode}
          address={editAddress}
          setAddress={setEditAddress}
          country={editCountry}
          setCountry={setEditCountry}
          city={editCity}
          setCity={setEditCity}
          state={editState}
          setState={setEditState}
          pincode={editPincode}
          setPincode={setEditPincode}
          // Error states
          nameError={nameError}
          phonenoError={phonenoError}
          addressError={addressError}
          countryError={countryError}
          cityError={cityError}
          stateError={stateError}
          pincodeError={pincodeError}
          // Form handlers
          onPressCountry={onPressEditCountry}
          onSubmitEdit={onSubmitEditAddress}
          onCancelEdit={onCancelEdit}
          buttonLoading={buttonLoading}
        />
      );
    },
    [addresses, selectedAddressId, editingAddressId, editName, editPhoneno, editCountryCode, editAddress, editCountry, editCity, editState, editPincode, nameError, phonenoError, addressError, countryError, cityError, stateError, pincodeError, buttonLoading]
  );

  return (
    <div className="address-page">
      <HomeHeader />
      <Breadcrumb
        items={[
          {
            label: "Home",
            path: "/",
          },
          {
            label: "Address",
            isActive: true,
          },
        ]}
      />
      <div className="page-content">
        {!isLoggedIn ? (
          <EmptyState
            icon={FaUser}
            title="Sign In to Continue"
            description="please login with your account. Enjoy a seamless shopping experience with personalized recommendations and more!"
            buttonText="Login"
            onButtonClick={() => navigate("/login")}
            iconSize={48}
          />
        ) : loading ? (
          <InlineLoader text="Loading addresses..." />
        ) : (
          <>
            {addresses.length > 0 ? (
              <div className="address-page-container">
                <div className="address-list">
                  {addresses.map(renderAddresses)}
                </div>

                {/* Collapsible Add Address Form */}
                <div className="add-address-section">
                  {!showAddForm && (
                    <button
                      className="add-address-toggle"
                      onClick={onAddNewAddress}
                      type="button"
                    >
                      <FiPlus className="add-icon" />
                      <span className="add-address-toggle-text">Add a new address</span>
                    </button>
                  )}

                  {showAddForm && (
                    <div className="add-address-form">
                      <div className="form-title">ADD A NEW ADDRESS</div>
                      {/* Row 1: Name and Contact */}
                      <div className="form-row">
                        <div className="form-field">
                          <CommonInput
                            value={name}
                            onChange={setName}
                            error={nameError}
                            editable={!buttonLoading}
                            placeholder="Enter name"
                          />
                        </div>
                        <div className="form-field">
                          <PhoneInput
                            code={countryCode}
                            onChangeCode={(country) => setCountryCode(country.dial_code)}
                            onChangeText={setPhoneno}
                            value={phoneno}
                            placeholder="Enter phone number"
                            error={phonenoError}
                            className="phone-input-compact"
                          />
                        </div>
                      </div>

                      {/* Row 2: Address */}
                      <div className="form-row">
                        <div className="form-field full-width">
                          <CommonInput
                            value={address}
                            onChange={setAddress}
                            error={addressError}
                            editable={!buttonLoading}
                            placeholder="Enter address"
                            multiline={true}
                          />
                        </div>
                      </div>

                      {/* Row 3: Country and State */}
                      <div className="form-row">
                        <div className="form-field">
                          <div className="country-container">
                            <button
                              className={`country-input-field ${countryError ? "error" : ""
                                }`}
                              onClick={onPressCountry}
                              type="button"
                            >
                              <span className={`country-text ${country ? "selected" : ""}`}>
                                {country ? country?.name : "Select country"}
                              </span>
                              <svg
                                width="16"
                                height="16"
                                viewBox="0 0 24 24"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                              >
                                <path
                                  d="M6 9L12 15L18 9"
                                  stroke="currentColor"
                                  strokeWidth="2"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                />
                              </svg>
                            </button>
                            {countryError && (
                              <div className="country-error">{countryError}</div>
                            )}
                          </div>
                        </div>
                        <div className="form-field">
                          <CommonInput
                            value={state}
                            onChange={setState}
                            error={stateError}
                            placeholder="Enter state"
                            editable={!buttonLoading}
                          />
                        </div>
                      </div>

                      {/* Row 4: City and Pincode */}
                      <div className="form-row">
                        <div className="form-field">
                          <CommonInput
                            value={city}
                            onChange={setCity}
                            error={cityError}
                            placeholder="Enter city"
                            editable={!buttonLoading}
                          />
                        </div>
                        <div className="form-field">
                          <CommonInput
                            value={pincode}
                            onChange={setPincode}
                            error={pincodeError}
                            editable={!buttonLoading}
                            placeholder="Enter pincode"
                          />
                        </div>
                      </div>

                      <div className="form-actions">
                        <CommonButton
                          title={buttonLoading ? "Saving..." : "Save"}
                          onPress={onSubmitAddress}
                          disabled={buttonLoading}
                          customClassName="save-address-button"
                        />
                        <CommonButton
                          title="Cancel"
                          onPress={onCancelForm}
                          disabled={buttonLoading}
                          customClassName="cancel-address-button"
                          outline={true}
                          outlined
                        />
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ) : !showAddForm ? (
              <EmptyState
                icon={FiMapPin}
                title="No Addresses Found"
                description="You don't have any addresses saved yet. Add your first address to get started."
                buttonText="Add New Address"
                onButtonClick={onAddNewAddress}
              />
            ) : (
              <div className="empty-state-add-form">
                <div className="empty-state-form-container">
                  <div className="empty-state-form-title">ADD YOUR FIRST ADDRESS</div>
                  {/* Row 1: Name and Contact */}
                  <div className="empty-state-form-row">
                    <div className="empty-state-form-field">
                      <CommonInput
                        value={name}
                        onChange={setName}
                        error={nameError}
                        editable={!buttonLoading}
                        placeholder="Enter name"
                      />
                    </div>
                    <div className="empty-state-form-field">
                      <PhoneInput
                        code={countryCode}
                        onChangeCode={(country) => setCountryCode(country.dial_code)}
                        onChangeText={setPhoneno}
                        value={phoneno}
                        placeholder="Enter phone number"
                        error={phonenoError}
                        className="phone-input-compact"
                      />
                    </div>
                  </div>

                  {/* Row 2: Address */}
                  <div className="empty-state-form-row">
                    <div className="empty-state-form-field full-width">
                      <CommonInput
                        value={address}
                        onChange={setAddress}
                        error={addressError}
                        editable={!buttonLoading}
                        placeholder="Enter address"
                        multiline={true}
                      />
                    </div>
                  </div>

                  {/* Row 3: Country and State */}
                  <div className="empty-state-form-row">
                    <div className="empty-state-form-field">
                      <div className="country-container">
                        <button
                          className={`country-input-field ${countryError ? "error" : ""
                            }`}
                          onClick={onPressCountry}
                          type="button"
                        >
                          <span className={`country-text ${country ? "selected" : ""}`}>
                            {country ? country?.name : "Select country"}
                          </span>
                          <svg
                            width="16"
                            height="16"
                            viewBox="0 0 24 24"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              d="M6 9L12 15L18 9"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        </button>
                        {countryError && (
                          <div className="country-error">{countryError}</div>
                        )}
                      </div>
                    </div>
                    <div className="empty-state-form-field">
                      <CommonInput
                        value={state}
                        onChange={setState}
                        error={stateError}
                        placeholder="Enter state"
                        editable={!buttonLoading}
                      />
                    </div>
                  </div>

                  {/* Row 4: City and Pincode */}
                  <div className="empty-state-form-row">
                    <div className="empty-state-form-field">
                      <CommonInput
                        value={city}
                        onChange={setCity}
                        error={cityError}
                        placeholder="Enter city"
                        editable={!buttonLoading}
                      />
                    </div>
                    <div className="empty-state-form-field">
                      <CommonInput
                        value={pincode}
                        onChange={setPincode}
                        error={pincodeError}
                        editable={!buttonLoading}
                        placeholder="Enter pincode"
                      />
                    </div>
                  </div>

                  <div className="empty-state-form-actions">
                    <CommonButton
                      title={buttonLoading ? "Saving..." : "Save Address"}
                      onPress={onSubmitAddress}
                      disabled={buttonLoading}
                      customClassName="empty-state-save-button"
                    />
                    <CommonButton
                      title="Cancel"
                      onPress={onCancelForm}
                      disabled={buttonLoading}
                      customClassName="empty-state-cancel-button"
                      outline={true}
                      outlined
                    />
                  </div>
                </div>
              </div>
            )}
          </>
        )}
        {processing && <FullscreenLoader text="Processing..." />}
        <ConfirmationModal
          visible={modalVisible}
          title="Delete Address"
          description="Are you sure you want to delete this address?"
          rightButtonText="Yes"
          leftButtonText="No"
          onRightButtonPress={deleteAddress}
          onLeftButtonPress={onCloseModal}
        />

        <CountryModal
          visible={countryModalVisible}
          onClose={onCloseCountryModal}
          onSelectCountry={onSelectCountryHandler}
          selectedCountry={editingAddressId ? editCountry : country}
          countryList={countryList}
        />
      </div>
      <CommonFooter />
    </div>
  );
};

export default Address;
